import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, Route, Routes } from "react-router-dom";
import storeHero from "../../assets/images/store-hero.png";
import CloseButton from "../../components/CloseButton";
import Loader from "../../components/Loader";
import Cart from "./Cart";
import Checkout from "./Checkout";
import Shopping from "./Shopping";
import "./store.scss";
import ThankYou from "./ThankYou";

function Store() {
  const isMobile = useSelector((state) => state.appSlice.isMobile);
  const guaranteeRef = useRef(null);
  const salesRef = useRef(null);
  const pickupRef = useRef(null);
  const [activeSections, setActiveSections] = useState([]);
  const [heroImageLoading, setHeroImageLoading] = useState(true);
  const [showPickupLocations, setShowPickupLocations] = useState(false);

  // Prevent background scrolling when mobile modal is open
  useEffect(() => {
    if (isMobile && showPickupLocations) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobile, showPickupLocations]);

  // FROM GOOGLE SHEETS
  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  const validCoupons = useSelector((state) => state.storeSlice.validCoupons);
  const storeData = useSelector((state) => state.storeSlice.googleSheetData);
  const status = useSelector((state) => state.storeSlice.status);
  const pickupLocations = useSelector(
    (state) => state.storeSlice.pickupLocations || [],
  );

  const hasAdvertisedCoupons = validCoupons.some(
    (coupon) => coupon.isAdvertised === "TRUE",
  );

  //Open sales section if there are advertised coupons
  useEffect(() => {
    if (hasAdvertisedCoupons) {
      setActiveSections((prev) => {
        if (!prev.includes("sales")) {
          return [...prev, "sales"];
        }
        return prev;
      });
    }
  }, [hasAdvertisedCoupons]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setHeroImageLoading(false);
    img.src = storeHero;

    // In case the image is already cached and the load event has fired
    // before the event handler was attached.
    if (img.complete) {
      setHeroImageLoading(false);
    }
  }, []);

  //END OF FROM GOOGLE SHEETS
  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  // Function to toggle accordion sections
  const toggleSection = (section) => {
    if (activeSections.includes(section)) {
      setActiveSections(activeSections.filter((s) => s !== section));
    } else {
      setActiveSections([...activeSections, section]);
    }
  };

  const renderAdvertisedCoupons = () => {
    return validCoupons
      .filter((coupon) => coupon.isAdvertised === "TRUE")
      .map((validCoupon, index) => (
        <div className="advertised-coupon-card" key={index}>
          <strong className="coupon-code">{validCoupon.code}</strong>
          <span className="coupon-description">{validCoupon.description}</span>
        </div>
      ));
  };

  return (
    <div className="content-container">
      {heroImageLoading && <Loader hasHeader={true} />}
      <div className="store-hero-container">
        <div className="store-title-container">
          <h2>Welcome to</h2>
          <h1 className="store-title">Our Store</h1>
        </div>
        <div className="hero-content-container">
          <div className="store-text-container">
            <p className="store-subtitle">
              We are thrilled to assist you in planting trees and perennials
              that will nourish you and your loved ones, contributing to the
              food resiliency of our communities!
              <br />
              <br /> We offer a wide selection of locally grown nut trees and
              berry bushes from Nova Scotia, along with fruit trees sourced from
              Ontario. Order now for May pickup!
            </p>
            {isMobile && (
              <>
                <div className="inline-store-hero-img">
                  <img
                    className="__store-hero"
                    src={storeHero}
                    alt="Store main"
                  />
                </div>
                <div className="pickup-locations-button-container mobile">
                  <button
                    className="pickup-locations-button"
                    onClick={() => setShowPickupLocations(!showPickupLocations)}
                  >
                    Pickup Locations
                  </button>
                </div>
                {showPickupLocations && (
                  <div
                    className="pickup-locations-modal-overlay"
                    onClick={() => setShowPickupLocations(false)}
                  >
                    <div
                      className="pickup-locations-modal"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="pickup-locations-modal-header">
                        <h3>Pickup Locations</h3>
                        <button
                          className="close-button"
                          onClick={() => setShowPickupLocations(false)}
                          aria-label="Close modal"
                        >
                          <CloseButton size={24} />
                        </button>
                      </div>
                      <div className="pickup-locations-list">
                        {pickupLocations.map((location, index) => (
                          <div key={index} className="pickup-location-item">
                            <span className="location-name">
                              {location.LOCATION}
                            </span>
                            <span className="location-details">
                              {location.DATE} - {location.TIME}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="accordion">
              {hasAdvertisedCoupons && (
                <div className="accordion-item">
                  <span
                    className="accordion-header"
                    onClick={() => toggleSection("sales")}
                  >
                    <span>Current Sales</span>
                    <span className="accordion-icon">
                      {activeSections.includes("sales") ? "−" : "+"}
                    </span>
                  </span>
                  <div
                    ref={salesRef}
                    className={`accordion-content ${
                      activeSections.includes("sales") ? "open" : ""
                    }`}
                    style={{
                      maxHeight:
                        activeSections.includes("sales") && salesRef.current
                          ? `${salesRef.current.scrollHeight}px`
                          : "0",
                    }}
                  >
                    <p>Input these coupon codes at checkout!</p>
                    <div className="advertised-coupon-list">
                      {renderAdvertisedCoupons()}
                    </div>
                  </div>
                </div>
              )}

              {/* Our Guarantee Section */}
              <div className="accordion-item">
                <span
                  className="accordion-header"
                  onClick={() => toggleSection("guarantee")}
                >
                  <span>Our Guarantee</span>
                  <span className="accordion-icon">
                    {activeSections.includes("guarantee") ? "−" : "+"}
                  </span>
                </span>
                <div
                  ref={guaranteeRef}
                  className={`accordion-content ${
                    activeSections.includes("guarantee") ? "open" : ""
                  }`}
                  style={{
                    maxHeight:
                      activeSections.includes("guarantee") &&
                      guaranteeRef.current
                        ? `${guaranteeRef.current.scrollHeight}px`
                        : "0",
                  }}
                >
                  <p>
                    If you take a picture of the purchased plant in the ground
                    with mulch (including a stake over 2ft tall) and send it to
                    us within 24hrs of receiving your order, we will guarantee
                    its survival and give you a replacement or full refund for
                    any that die within the first month.
                  </p>
                </div>
              </div>
              <div className="accordion-item">
                <span
                  className="accordion-header"
                  onClick={() => toggleSection("pickup")}
                >
                  <span>Spring 2026 Pickup</span>
                  <span className="accordion-icon">
                    {activeSections.includes("pickup") ? "−" : "+"}
                  </span>
                </span>
                <div
                  ref={pickupRef}
                  className={`accordion-content ${
                    activeSections.includes("pickup") ? "open" : ""
                  }`}
                  style={{
                    maxHeight:
                      activeSections.includes("pickup") && pickupRef.current
                        ? `${pickupRef.current.scrollHeight}px`
                        : "0",
                  }}
                >
                  <p>
                    The purchase of all items in this store are for pickup in
                    May 2026. Preordering for spring pick up is now live!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {!isMobile && (
            <div className="store-hero-right">
              <img className="__store-hero" src={storeHero} alt="Store main" />
              <div className="sale-banner">
                <h2>Pre-Order Sale</h2>
              </div>
              <div className="pickup-locations-button-container">
                <button
                  className="pickup-locations-button"
                  onClick={() => setShowPickupLocations(!showPickupLocations)}
                >
                  Pickup Locations
                </button>
                {showPickupLocations && (
                  <div
                    className="pickup-locations-popover"
                    onMouseEnter={() => setShowPickupLocations(true)}
                  >
                    <h3>Pickup Locations</h3>
                    <div className="pickup-locations-list">
                      {pickupLocations.map((location, index) => (
                        <div key={index} className="pickup-location-item">
                          <span className="location-name">
                            {location.LOCATION}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="store-content-container">
        <Routes>
          <Route index element={<Shopping storeData={storeData} />} />
          <Route path="review-cart" element={<Cart />} />
          <Route path="thank-you" element={<ThankYou />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="*" element={<Outlet />} />
        </Routes>
      </div>
    </div>
  );
}

export default Store;
